/**
 * TOSS Web Application — Client portal: coming back means coming back to the same PLACE
 *
 * «Назад» now returns the customer to the screen they came from. On a long list that is only half
 * the promise: the screen was forty cards down, and it comes back at the top.
 *
 * React Router's own `<ScrollRestoration>` cannot be used here. It restores `window.scrollY`, and
 * the portal has two scrollers depending on width: on a phone the DOCUMENT scrolls, while at ≥768
 * the root is `h-dvh overflow-hidden` and `<main>` is what moves. Whichever one is not the
 * scroller sits at 0, so both are saved and both are restored — the write to the wrong one is a
 * no-op.
 *
 * **Restored only on POP.** A push is a new place and belongs at the top; that reset used to live
 * in `ClientLayout` and moved here so one piece of code decides where a screen starts.
 *
 * **The hard part is that the content is not there yet.** A list arrives from react-query one or
 * two frames after the route does, and a scroll applied to an empty page lands at 0. So the target
 * is re-applied every frame until it sticks, the deadline runs out, or the customer touches the
 * screen — whichever comes first. That last one matters: a restoration that keeps yanking the page
 * back while somebody is already scrolling is worse than none.
 *
 * Positions live in `sessionStorage`, keyed by the history INDEX — `history.state.idx`, the
 * counter the router keeps per entry.
 *
 * NOT `location.key`, which was the first attempt and looked equivalent right up until the stand:
 * a `replace` mints a NEW key for the SAME entry, and both lists mirror their search text into the
 * address exactly that way. The position was written down under a key that no longer existed a
 * moment later, and coming back always landed at the top. The index is what does not move under a
 * replace — it identifies the PLACE rather than a router state.
 */

import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { historyIndex } from '@/utils/appHistory';

const STORAGE_KEY = 'toss.client.scroll';

/** How long to keep re-applying while the content loads. */
const RESTORE_DEADLINE_MS = 1200;

/** Close enough — a fractional pixel difference is not worth another frame. */
const ARRIVED_EPSILON = 2;

/**
 * Entries kept. A session walking thirty screens deep is already unusual, and the map is rewritten
 * on every navigation, so this is about keeping the write small rather than about memory.
 */
const MAX_ENTRIES = 30;

interface ScrollPosition {
  /** `<main>`, the scroller at ≥768. */
  main: number;
  /** The document, the scroller on a phone. */
  win: number;
}

function readAll(): Record<string, ScrollPosition> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, ScrollPosition>) : {};
  } catch {
    // Private mode, quota, an embedded webview — remembering is a convenience, not a feature.
    return {};
  }
}

function writeAll(map: Record<string, ScrollPosition>): void {
  try {
    const keys = Object.keys(map);
    const trimmed =
      keys.length <= MAX_ENTRIES
        ? map
        : Object.fromEntries(keys.slice(keys.length - MAX_ENTRIES).map((k) => [k, map[k]]));

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    /* see readAll */
  }
}

export interface UseClientScrollRestorationOptions {
  /**
   * Off for a screen that owns its own scroll position — the chat, which is pinned to the newest
   * message and would fight this.
   */
  enabled: boolean;
}

export function useClientScrollRestoration(
  scroller: RefObject<HTMLElement>,
  { enabled }: UseClientScrollRestorationOptions
): void {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Read on every render rather than remembered: it changes with the entry, and a `replace` —
  // which does re-render — deliberately leaves it alone. `location` is the trigger for that
  // re-read and nothing else.
  void location;
  const key = String(historyIndex() ?? 0);

  /**
   * Positions BY ENTRY, tracked as they change.
   *
   * Two things this shape fixes, and the tests found both — neither is visible by reading:
   *
   * 1. The position is not read from the DOM when the screen is left. By the time an effect's
   *    cleanup runs React has already committed the next screen, the old element is gone and the
   *    ref points at nothing, so every entry was saved as 0.
   * 2. It is a map and not «the latest position», because React's order on a navigation is:
   *    cleanup of old layout effects → NEW layout effects → cleanup of old passive effects. The
   *    restore for the incoming entry therefore runs BEFORE the outgoing entry's save, and a
   *    single slot was already overwritten by the time the save read it.
   */
  const positionsRef = useRef<Record<string, ScrollPosition>>({});

  // ── Remember where this entry was left ────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const element = scroller.current;

    const track = (): void => {
      // Only while this is still the entry we are standing on.
      //
      // Found on the stand, and invisible in a unit test: leaving a screen scrolls the NEXT one
      // to the top, and that scroll fires while this listener is still attached — React detaches
      // passive effects after it has committed and run the new screen's layout effects. So the
      // outgoing entry recorded the incoming screen's zero, and every return landed at the top.
      if (String(historyIndex() ?? 0) !== key) return;

      positionsRef.current[key] = { main: element?.scrollTop ?? 0, win: window.scrollY };
    };

    const save = (): void => {
      // Nothing tracked for this entry means it was never scrolled: the top is the truth.
      const map = readAll();
      map[key] = positionsRef.current[key] ?? { main: 0, win: 0 };
      writeAll(map);
    };

    element?.addEventListener('scroll', track, { passive: true });
    window.addEventListener('scroll', track, { passive: true });
    // `pagehide` rather than `beforeunload`: it fires for a tab going into the back/forward cache
    // too, which is the case where the position is most likely to be wanted back.
    window.addEventListener('pagehide', save);

    return () => {
      save();
      element?.removeEventListener('scroll', track);
      window.removeEventListener('scroll', track);
      window.removeEventListener('pagehide', save);
    };
  }, [enabled, key, scroller]);

  // ── Put the customer back where they were, or at the top ──────────────────
  useLayoutEffect(() => {
    if (!enabled) return;

    // A REPLACE is the SAME place with a different address — a list mirroring its search text,
    // a filter written into the query. Touching the scroll there undoes whatever was just
    // restored, which is exactly what happened on the stand: the position came back and the
    // mirror effect's replace threw it away one frame later.
    if (navigationType === 'REPLACE') return;

    const element = scroller.current;
    const target = navigationType === 'POP' ? readAll()[key] : undefined;

    if (!target) {
      // A new place starts at the top. Both scrollers, for the reason in the module comment,
      // and only when they are not there already — a redundant `scrollTo` still fires a scroll
      // event, and every listener on the page has to reason about it.
      if (element && element.scrollTop !== 0) element.scrollTop = 0;
      if (window.scrollY !== 0) window.scrollTo(0, 0);
      positionsRef.current[key] = { main: 0, win: 0 };
      return;
    }

    positionsRef.current[key] = target;

    let frame = 0;
    let done = false;
    const deadline = performance.now() + RESTORE_DEADLINE_MS;

    const stop = (): void => {
      if (done) return;
      done = true;
      cancelAnimationFrame(frame);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };

    const apply = (): void => {
      if (done) return;

      const current = scroller.current;
      if (current) current.scrollTop = target.main;
      window.scrollTo(0, target.win);

      const mainArrived = !current || Math.abs(current.scrollTop - target.main) <= ARRIVED_EPSILON;
      const winArrived = Math.abs(window.scrollY - target.win) <= ARRIVED_EPSILON;

      if ((mainArrived && winArrived) || performance.now() > deadline) {
        stop();
        return;
      }
      frame = requestAnimationFrame(apply);
    };

    // The customer's own scrolling always wins.
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);

    apply();

    return stop;
  }, [enabled, key, navigationType, scroller]);
}

export default useClientScrollRestoration;
